/**
 * Data-analysis query keys (Server-state rebuild, P4-tail). Preserves today's exact values.
 *
 * Keys follow the id-before-constant convention so an audit base key is a
 * strict prefix of its children (cascading invalidation). `ledger` hangs under
 * a specific `viewId`. Its optional `query` segment is the stable-serialized
 * filter/sort/group/pivot config so distinct configurations cache separately.
 */
export const DATA_ANALYSIS_ROOT = 'data-analysis';

/**
 * The last segment of every view's Documentation-drawer key (`viewAnnotations`). The key
 * hangs under its view, so no prefix names every view's drawer at once; the predicates
 * that need that (`isViewAnnotationsKey`, `isDataAnalysisViewReadKey`) match on this.
 */
export const VIEW_ANNOTATIONS_SEGMENT = 'annotations';
/** The key segment that marks a validation view's FURTHER page (`versionValidationViewPage`). */
export const VALIDATION_VIEW_PAGE_SEGMENT = 'page';

export const dataAnalysisKeys = {
  base: (id?: string) => ['data-analysis', id] as const,
  latestImport: (auditId?: string) => ['data-analysis', auditId, 'imports', 'latest'] as const,
  /**
   * Every run of the audit's data source, newest first (`GET /imports`) — read ONLY to
   * resolve the run that owns an open composition when that run is not the latest one.
   * Under the `imports` prefix beside `latest`, so a version exit's `base` invalidation
   * reaches it; nothing polls it.
   */
  importRuns: (auditId?: string) => ['data-analysis', auditId, 'imports', 'runs'] as const,
  /**
   * ONE run's own files (`GET /imports/{jobId}/delivery`, #476), keyed on the run's
   * `wizard.revision` as well as its id: every write that can change the files
   * (enqueue, reprocess, Date-coverage recompose) moves the revision, so a new answer
   * gets a new key and nothing has to invalidate it. Under `imports`, so a version
   * exit's `base` invalidation still reaches it.
   */
  importDelivery: (auditId?: string, jobId?: number | null, revision?: number | null) =>
    ['data-analysis', auditId, 'imports', 'delivery', jobId ?? null, revision ?? null] as const,
  /**
   * ONE gated evaluation's evidence (`GET /imports/{jobId}/gate-evidence`, #1020), keyed on
   * the evaluation it is about: a later gate on the same run is a new answer. Under
   * `imports`, so a version exit's `base` invalidation reaches it.
   */
  importGateEvidence: (auditId?: string, jobId?: number | null, evaluationNumber?: number | null) =>
    [
      'data-analysis',
      auditId,
      'imports',
      'gate-evidence',
      jobId ?? null,
      evaluationNumber ?? null,
    ] as const,
  /**
   * The period ONE run works on (`GET /imports/{jobId}/period`, #442). Keyed on the run
   * alone, not its revision: a colleague's settings save moves the run's `drift` without
   * moving the run, so the answer must stay refetchable. The hold writes its own answer
   * through. Under `imports`, so a version exit's invalidation reaches it.
   */
  importPeriod: (auditId?: string, jobId?: number | null) =>
    ['data-analysis', auditId, 'imports', 'period', jobId ?? null] as const,
  /**
   * NOT A READ — a cache marker. The last job outcome the socket echo re-read the run's
   * world for (`{ jobId, status }`), written by `useSyncAuditData` and read by the
   * wizard's poll-side backstop, so a transition both paths observe is refetched once.
   */
  importOutcomeEcho: (auditId?: string) =>
    ['data-analysis', auditId, 'imports', 'outcome-echo'] as const,
  /**
   * NOT A READ — a cache marker. This tab's own `POST /validate` that just answered
   * (`{ datasetVersionId, until }`), written by `useValidateVersion.onSuccess` and read
   * by `useSyncAuditData`'s `validation-run` arm, so the run's own echo — which lands
   * AFTER the answer, outside the `isMutating` window — is not a second re-read of the
   * version. Under `imports`, beside `outcome-echo`, and deliberately NOT under
   * `versions`: the version prefix is what the wizard invalidates and refetches.
   */
  validationRunEcho: (auditId?: string) =>
    ['data-analysis', auditId, 'imports', 'validation-run-echo'] as const,
  /**
   * NOT A READ — a cache marker, the Finding family's twin of `validation-run-echo`:
   * this tab's own Finding or journal write that just answered (`{ until }`), written
   * by every `findings.hooks` mutation's `onSuccess` and read by `useSyncAuditData`'s
   * `findingChanged` arm. Audit-scoped, not version-scoped, because that echo names
   * neither a version nor a Finding — `{ action: 'findingChanged' }` is the whole context.
   */
  findingWriteEcho: (auditId?: string) =>
    ['data-analysis', auditId, 'imports', 'finding-write-echo'] as const,
  /**
   * NOT A READ — a cache marker. The last version exit a COLLEAGUE's tab announced
   * (`{ datasetVersionId }`, a confirm or a cancel), written by
   * `useSyncAuditData` before it re-reads anything and read by the open wizard, so a
   * run closed elsewhere stops offering its confirm the moment the echo lands (#506),
   * not one round trip later when the history is re-read.
   */
  versionExitEcho: (auditId?: string) =>
    ['data-analysis', auditId, 'imports', 'version-exit-echo'] as const,
  dataSources: (auditId?: string) => ['data-analysis', auditId, 'data-sources'] as const,

  /**
   * The re-openable import record for ONE dataset version (CAP-024).
   *
   * VERSION-keyed, not job-keyed, because the record is: the bexio overlay mints
   * no job, and a failed job never records a version id. Under the audit root, so
   * the `data-analysis-import` socket echo — which invalidates the whole subtree —
   * already reaches it; a record read while a version was still composing must not
   * outlive the composition.
   */
  importRecord: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'record'] as const,

  /**
   * EVERYTHING read about ONE dataset version — record, validation (+ views), diff,
   * coverage, mapping census/statement, correction journals. The prefix the
   * version-scoped socket echoes (`validation-run`, `exception-assessed`) invalidate,
   * so a peer's re-validate refetches that version and not the whole workspace.
   */
  version: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId] as const,
  /**
   * Every version-scoped read of the audit at once — the invalidation handle a JOB
   * OUTCOME takes (`useSyncAuditData`): a composition landed, halted or was dropped,
   * and the socket names no version for a halt or a failure, so the run's whole
   * world is re-read while the workspace's (views, tags, symbols, the active diff)
   * is left alone.
   */
  versions: (auditId?: string) => ['data-analysis', auditId, 'versions'] as const,
  /**
   * Which properties the data set's ACTIVE version holds no value for. Under that
   * version, so every version-scoped invalidation reaches it — a job outcome, a
   * colleague's composing journal — and a new active version is a new key.
   */
  propertyAvailability: (auditId: string | undefined, versionId: number, dataSetId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'property-availability', dataSetId] as const,
  /** Every property-availability answer about one version — what a composition re-reads. */
  propertyAvailabilityOfVersion: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'property-availability'] as const,

  /**
   * The version's correction journals — the list, and with it the `editorContext`
   * (the account chart + coverage window the journal editor is built from). Under
   * the version, because the window is a fact about THAT composition and widens when
   * a retired journal is reactivated — a status change must re-read it.
   */
  versionCorrectionJournals: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'correction-journals'] as const,

  /** The engagement's Findings — the list root, and one Finding's detail under it. */
  findings: (auditId?: string) => ['data-analysis', auditId, 'findings'] as const,
  /** One correction journal's own log (§4.3) — read on demand, per journal. */
  correctionJournalHistory: (auditId?: string, correctionJournalId?: number) =>
    ['data-analysis', auditId, 'correction-journals', correctionJournalId, 'history'] as const,
  /**
   * The Add-finding picker's own walk of `GET /findings` — UNDER the findings key, so
   * every write that invalidates the list reaches it, and keyed on the search term,
   * because a narrowed page is a different list and must not be served from the cache
   * of another one. Its cached value is pages, not rows; the optimistic title writes
   * belong to `findings` above and must not be pointed here.
   */
  findingPicker: (auditId?: string, search = '') =>
    ['data-analysis', auditId, 'findings', 'picker', search] as const,
  /** Every search's walk at once — what a finding write invalidates, since a new or
   *  renamed finding can enter or leave any of them. */
  findingPickerRoot: (auditId?: string) =>
    ['data-analysis', auditId, 'findings', 'picker'] as const,
  finding: (auditId: string | undefined, findingId: number) =>
    ['data-analysis', auditId, 'findings', findingId] as const,

  /** The files already in the data set, with row-level provenance — Coverage
   *  review's already-in-the-dataset lane. */
  dataSetFiles: (auditId: string | undefined, dataSetId: number) =>
    ['data-analysis', auditId, 'data-sources', dataSetId, 'files'] as const,

  /** Which of these library documents the data set holds (#645), by the sorted ids. */
  documentProvenance: (auditId: string | undefined, dataSetId: number, documentIds: string) =>
    [
      'data-analysis',
      auditId,
      'data-sources',
      dataSetId,
      'document-provenance',
      documentIds,
    ] as const,

  /** The data set's version history — where a job-less pending composition is found. */
  versionHistory: (auditId: string | undefined, dataSetId: number) =>
    ['data-analysis', auditId, 'data-sources', dataSetId, 'versions'] as const,

  /** What restoring ONE version would cost — taken against the current incumbent, so
   *  it lives under the data-analysis root that every activation re-reads. */
  versionRestorePreview: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'restore-preview'] as const,
  /** What *Restore v0* would cost — v0 has no row, so it is keyed by its data set. */
  emptyStartRestorePreview: (auditId: string | undefined, dataSetId: number) =>
    ['data-analysis', auditId, 'data-sources', dataSetId, 'restore-v0-preview'] as const,
  /** A restore's or a reopen's operation (#408) — polled until it completes or fails. */
  restoreOperation: (auditId: string | undefined, operationId: number) =>
    ['data-analysis', auditId, 'restore-operations', operationId] as const,

  /** §4.4's checks + the armed activation gate for ONE version. */
  versionValidation: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'validation'] as const,

  /**
   * FEAT-14's validation views. Under the version's `validation` key so a
   * re-validate invalidates the tiles with the verdicts they render.
   */
  versionValidationViews: (auditId: string | undefined, versionId: number) =>
    [...dataAnalysisKeys.versionValidation(auditId, versionId), 'views'] as const,
  /**
   * One validation view's read. `overrides` — the session config a regular table was
   * given from its header (#638), serialized — keys a read of its own UNDER the view's
   * key, so every invalidation of the view still reaches it.
   */
  versionValidationView: (
    auditId: string | undefined,
    versionId: number,
    viewId: string,
    overrides?: string,
  ) =>
    overrides
      ? ([
          ...dataAnalysisKeys.versionValidationViews(auditId, versionId),
          viewId,
          overrides,
        ] as const)
      : ([...dataAnalysisKeys.versionValidationViews(auditId, versionId), viewId] as const),
  /**
   * A FURTHER PAGE of one validation view's read — the page at `cursor`, read with the
   * same `overrides` — under the view's key like its first page, but marked `page` so
   * nothing that stands in a view's latest first page can pick it up.
   */
  versionValidationViewPage: (
    auditId: string | undefined,
    versionId: number,
    viewId: string,
    overrides: string | undefined,
    cursor: string,
  ) =>
    [
      ...dataAnalysisKeys.versionValidationViews(auditId, versionId),
      viewId,
      VALIDATION_VIEW_PAGE_SEGMENT,
      overrides ?? null,
      cursor,
    ] as const,
  /** The Review step's diff. `changeType` caches each class's page separately; the
   *  summary (no class named) has its own entry. */
  versionDiff: (auditId: string | undefined, versionId: number, changeType?: string) =>
    changeType
      ? (['data-analysis', auditId, 'versions', versionId, 'diff', changeType] as const)
      : (['data-analysis', auditId, 'versions', versionId, 'diff'] as const),

  /**
   * The Map-accounts step's coverage census for a pending version. Under the
   * version rather than under `mapping`, because it is a fact about THAT
   * composition — a different pending version has a different census.
   *
   * KEYED ON THE UNSAVED-EDITS PAYLOAD when one rides, for the same reason the
   * statement beside it is: the census is computed THROUGH those edits, so two reads
   * of one version with different batches are two different answers to the gate
   * question and must not share a cache entry.
   *
   * ⚠ THE SEGMENT IS OMITTED, NOT EMPTIED, when no payload rides — the two-argument
   * form is the PREFIX every folded entry hangs under (`useStageMapping` patches the
   * `staged` arm of all of them through it; a colleague's `mapping-staged` echo
   * invalidates them through it), and a `''` placeholder would stop it prefix-matching
   * the folded entries it must reach.
   *
   * `held` — WHAT THE PAYLOAD LEFT OUT BECAUSE THE STORE HOLDS IT (`readPayloadsOf`).
   * The payload alone no longer restates the batch, so without it a pick whose write
   * landed would ask under the PRE-pick key, and the production cache (5-minute
   * `staleTime`) would answer with the pre-pick verdict. Appended only when something
   * was left out, so with nothing held the key is exactly the payload-only one.
   */
  mappingCoverage: (
    auditId: string | undefined,
    versionId: number,
    assignments?: string,
    held?: string | null,
  ) =>
    assignments === undefined
      ? (['data-analysis', auditId, 'versions', versionId, 'mapping-coverage'] as const)
      : held == null
        ? ([
            'data-analysis',
            auditId,
            'versions',
            versionId,
            'mapping-coverage',
            assignments,
          ] as const)
        : ([
            'data-analysis',
            auditId,
            'versions',
            versionId,
            'mapping-coverage',
            assignments,
            { held },
          ] as const),

  /**
   * The Map-accounts STATEMENT for a pinned version.
   *
   * KEYED ON THE UNSAVED-EDITS PAYLOAD TOO, unlike the census beside it: the statement
   * is drawn THROUGH those edits, so two reads of one version with different batches
   * are two different pictures and must not share a cache entry. `assignments` is the
   * serialised payload (or `''` when none rides), which makes the key change exactly
   * when the drawn statement would.
   */
  /**
   * EVERY batch's statement for one version — the prefix, for invalidating them all.
   *
   * It exists because the key below carries the serialised batch, so a caller that has
   * to invalidate "the statement, whatever batch is on screen" cannot name the entry it
   * wants. A placed annotation is exactly that caller: the mark arrives on the read's
   * own legend, and the batch it was placed over is not the invalidator's business.
   */
  /**
   * NOT A QUERY — the MUTATION key of `PUT /mapping/staged` (`useStageMapping`), so the
   * socket layer can ask `isMutating` whether THIS tab has a staged write in flight
   * when that write's own echo comes back to it. Named here, beside the census it
   * would otherwise refetch, so the two sides of that dedupe share one value.
   */
  stageMappingWrite: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'mapping-staged-write'] as const,
  /**
   * The MUTATION key of `POST /versions/{id}/validate` (`useValidateVersion`), for the
   * same reason: its `validation-run` echo is emitted inside the handler and would
   * re-read the whole version while the actor's answer is still in flight.
   */
  validateVersionWrite: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'validate-write'] as const,
  /**
   * The MUTATION key of a validation drill's spawn and dismissal
   * (`useCreateValidationDrill` / `useDismissValidationDrill`): their `validation_drill`
   * echo is emitted inside the handler, so it reaches the actor while the answer that
   * re-reads the tile list is still in flight.
   */
  validationDrillWrite: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'validation-drill-write'] as const,
  /**
   * The MUTATION key of `PUT /imports/{jobId}/wizard-step` (`useSetWizardStep`): a
   * wizard reopened while this tab's last pointer write is in flight waits for it
   * before it lands (#582).
   */
  wizardStepWrite: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'wizard-step-write'] as const,
  /**
   * The MUTATION key every Finding and correction-journal write carries (`findings.hooks`),
   * for the same reason again: the `findingChanged` echo would re-read the whole version
   * — plus the Findings list, every journal list and every backlink read — while the
   * answer's own `onSuccess` re-reads exactly what moved. This key is the first line of
   * that dedupe; `findingWriteEcho` above is the second, for the echo that lands AFTER
   * the answer (every `findingChanged` handler broadcasts after `res.json`).
   */
  findingWrite: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'finding-write'] as const,
  /**
   * The journal FIELD and STATUS writes (`usePatchCorrectionJournal`,
   * `useSetCorrectionJournalStatus`) — the two `findingWrite`s that can move a version's
   * composition, and with it re-evaluate its checks inside their own request. The
   * Validate step counts them as evaluation work in flight (`useComposingJournalWrite`).
   *
   * ⚠ UNDER `findingWrite`, NEVER BESIDE IT: the socket layer's own-echo dedupe asks
   * `isMutating` on the prefix, and a key outside it would read this tab's journal
   * writes as a colleague's.
   */
  findingJournalWrite: (auditId: string | undefined) =>
    [...dataAnalysisKeys.findingWrite(auditId), 'journal'] as const,
  /**
   * The MUTATION key of `POST /versions/{id}/confirm` and `/cancel` (`useConfirmImportVersion`,
   * `useCancelImportVersion`) — the two exits from `pending`. Their lifecycle echo
   * (`status: 'confirmed' | 'cancelled'`) is emitted after the commit and before the
   * answer, and both it and the answer's `onSuccess` take the WHOLE subtree; without
   * the key the actor paid for every data-analysis read twice, with the pivot cancelled
   * mid-flight between the two (2026-09-18).
   */
  versionExitWrite: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'version-exit-write'] as const,
  mappingStatements: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'mapping-statement'] as const,
  /**
   * `changes`: the read also carries what a later step moved (`include=changedIntersections`).
   * `held`: what the payload left out because the store holds it — the same reason, and
   * the same omission when nothing was, as on `mappingCoverage`.
   * `chip`: the Audit areas chip (#1079) is its own segment, last, so its tree never
   * answers for the Financial statement's under one key. The Financial statement's key is
   * unchanged, and both stay under `mappingStatements`, which every pick invalidates.
   */
  mappingStatement: (
    auditId: string | undefined,
    versionId: number,
    assignments = '',
    changes = false,
    held?: string | null,
    chip: 'financialStatement' | 'auditAreas' = 'financialStatement',
  ) => {
    const asked = changes
      ? ([
          ...dataAnalysisKeys.mappingStatements(auditId, versionId),
          assignments,
          'changedIntersections',
        ] as const)
      : ([...dataAnalysisKeys.mappingStatements(auditId, versionId), assignments] as const);
    const keyed = held == null ? asked : ([...asked, { held }] as const);
    return chip === 'auditAreas' ? ([...keyed, { chip }] as const) : keyed;
  },

  /** The Date-coverage picture — bars, lanes, and the checksum both exits from
   *  `pending` compare against. Keyed under the version for the same reason the
   *  census is: it is a fact about THAT composition. */
  versionCoverage: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'coverage'] as const,

  /**
   * The import record's Map-accounts statement (#558). Its own segment, NOT under
   * `mappingStatements`: that prefix is the live step's, which every pick and confirm
   * invalidates, and the record is frozen with the version.
   */
  versionMappingStatement: (auditId: string | undefined, versionId: number) =>
    ['data-analysis', auditId, 'versions', versionId, 'mapping-statement-record'] as const,

  // Views workspace.
  views: (auditId?: string) => ['data-analysis', auditId, 'views'] as const,
  view: (auditId: string | undefined, viewId: string) =>
    ['data-analysis', auditId, 'views', viewId] as const,
  ledger: (auditId: string | undefined, viewId: string, query?: string) =>
    query
      ? (['data-analysis', auditId, 'views', viewId, 'ledger', query] as const)
      : (['data-analysis', auditId, 'views', viewId, 'ledger'] as const),
  /**
   * Where one placement's row ranks in a view's window (the read's `probe` mode).
   *
   * Under `ledger` on purpose: a rank is a fact about that window's reading order, so
   * every invalidation that moves the rows — a config save, a re-import — must take
   * the positions with it.
   */
  placementProbe: (auditId: string | undefined, viewId: string, placementId: number) =>
    [...dataAnalysisKeys.ledger(auditId, viewId), 'probe', placementId] as const,

  /**
   * The data source's placements, optionally WIDENED to one version's run-scoped marks.
   *
   * VERSION-KEYED, because naming a version changes what the list contains: by default
   * it is the engagement plane, and `?datasetVersionId=` adds that version's staged and
   * frozen marks. Two reads with different versions are two different lists and must
   * not share a cache entry — `''` is the plain engagement read.
   */
  placements: (auditId: string | undefined, dataSourceId: number, versionId?: number) =>
    [
      'data-analysis',
      auditId,
      'data-sources',
      dataSourceId,
      'placements',
      versionId ?? '',
    ] as const,
  /** The view's dataset-wide line count (selection-summary, filtered mode). Under
   *  the view so a view-scoped invalidation refreshes it with the rows. `query` is a
   *  drill's edited cards, as on `ledger`. */
  viewRowCount: (auditId: string | undefined, viewId: string, query?: string) =>
    query
      ? (['data-analysis', auditId, 'views', viewId, 'row-count', query] as const)
      : (['data-analysis', auditId, 'views', viewId, 'row-count'] as const),
  /** The Documentation drawer. Hangs under the view so a view-scoped invalidation
   *  refreshes the legend with it; every view's at once is `isViewAnnotationsKey`. */
  viewAnnotations: (auditId: string | undefined, viewId: string) =>
    ['data-analysis', auditId, 'views', viewId, VIEW_ANNOTATIONS_SEGMENT] as const,
  /** The combined activity feed (view plane + engagement-wide annotation plane).
   *  Under the view, so any view-scoped invalidation refreshes it too. */
  viewActivity: (auditId: string | undefined, viewId: string) =>
    ['data-analysis', auditId, 'views', viewId, 'activity'] as const,
  /** Inbound references to this view ("what links here"). Under the view. */
  viewBacklinks: (auditId: string | undefined, viewId: string) =>
    ['data-analysis', auditId, 'views', viewId, 'backlinks'] as const,
  /**
   * The categorical picker's option set. Keyed on the SEARCH TERM as well as the
   * dimension: the server narrows the population itself, so two terms are two
   * different answers and must not share a cache entry.
   */
  dimensionValues: (auditId: string | undefined, viewId: string, dimension: string, search = '') =>
    ['data-analysis', auditId, 'views', viewId, 'dimension-values', dimension, search] as const,
  /** Every value list asked of one view, whatever the dimension or term. */
  dimensionValuesRoot: (auditId: string | undefined, viewId: string) =>
    ['data-analysis', auditId, 'views', viewId, 'dimension-values'] as const,
  /** `dimension` separates the two numeric targets, which have different ranges. */
  histogram: (auditId: string | undefined, viewId: string, dimension: string, query?: string) =>
    query
      ? (['data-analysis', auditId, 'views', viewId, 'histogram', dimension, query] as const)
      : (['data-analysis', auditId, 'views', viewId, 'histogram', dimension] as const),

  // Annotation plane. Symbols and tags are ENGAGEMENT-scoped, not view-scoped:
  // one meaning per symbol across every surface, so they must not cache per view.
  symbols: (auditId?: string) => ['data-analysis', auditId, 'symbols'] as const,
  symbol: (auditId: string | undefined, symbolId: number) =>
    ['data-analysis', auditId, 'symbols', symbolId] as const,
  /** "n in other views" from one view. Under `symbols`, so the annotation echo that
   *  re-reads the symbol list refreshes an open popover's footprint with it. */
  symbolViews: (auditId: string | undefined, symbolId: number, excludeViewId: string) =>
    ['data-analysis', auditId, 'symbols', symbolId, 'views', excludeViewId] as const,
  // Journal-entry tags.
  tags: (auditId?: string) => ['data-analysis', auditId, 'tags'] as const,

  /** The re-import diff. */
  dataSourceChanges: (auditId: string | undefined, dataSourceId: number) =>
    ['data-analysis', auditId, 'data-sources', dataSourceId, 'changes'] as const,

  /** Selection totals. `selectionKey` is the stable-serialized line-id set — the
   *  top-line checksum (no selection) caches under its own key. */
  selectionSummary: (auditId: string | undefined, selectionKey: string) =>
    ['data-analysis', auditId, 'selection-summary', selectionKey] as const,
  /**
   * The mutation SCOPE every config save of the audit shares, so they run one at a
   * time: each is a whole-config, last-write-wins PATCH with no concurrency token.
   */
  viewConfigSaveScope: (auditId: string | undefined) => `data-analysis:${auditId}:view-config`,
  /**
   * NOT A READ — the chip-bar reorder's mutation key. It lets an answer, a refusal and
   * the route's own echo see that a later reorder of this tab is still out.
   */
  viewReorderWrite: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'view-reorder-write'] as const,
  /**
   * The mutation SCOPE every chip-bar reorder of the audit shares, so they run one at a
   * time: each is a full permutation built on the order the one before it drew.
   */
  viewReorderScope: (auditId: string | undefined) => `data-analysis:${auditId}:view-reorder`,
  /**
   * The mutation SCOPE every retirement ruling of the audit shares, so they run one at a
   * time: each takes the audit's mutation lock, and picks in a row would contend for it.
   */
  retirementRulingScope: (auditId: string | undefined) =>
    `data-analysis:${auditId}:retirement-ruling`,
  /**
   * Every selection total of the audit — the invalidation handle, not a query. A
   * whole-set entry is keyed by its predicate (`{viewId, …}`), which names the view
   * and not its config, so a config save or a views echo must drop them all.
   */
  selectionSummaryRoot: (auditId: string | undefined) =>
    ['data-analysis', auditId, 'selection-summary'] as const,

  // CAP-027 account mapping. ENGAGEMENT-scoped, not view-scoped: the registry is a
  // per-audit snapshot of the platform catalogue (taken lazily on first read; it changes
  // only through an audited edit, a roll-forward or an import's re-seed of an untouched
  // seed, each of which broadcasts `reason: 'registry'`), and one account's assignment is
  // the same fact on every view that shows it.
  /**
   * Everything account-mapping for this audit — the invalidation handle, not a query.
   *
   * A remote NODE EDIT (rename / reorder), a roll-forward and an import's RE-SEED (#431
   * v7) broadcast `data-analysis-views` with `context.reason === 'registry'`, and the
   * views key does NOT reach the registry:
   * `mappingRegistry` is a SIBLING of `views`, not a descendant. This prefix covers both
   * the registry and any future mapping read, which is what the socket handler invalidates.
   */
  mapping: (auditId?: string) => ['data-analysis', auditId, 'mapping'] as const,
  /** The Report hierarchy + audit-area set for this audit. */
  mappingRegistry: (auditId?: string) => ['data-analysis', auditId, 'mapping', 'registry'] as const,
  /**
   * Every account's assignment (`GET /mapping`) — the value picker's account → report-line
   * parents (#640). Under `mapping`, so a registry echo re-reads it with the tree.
   */
  mappingAssignments: (auditId?: string) =>
    ['data-analysis', auditId, 'mapping', 'assignments'] as const,
} as const;
